import { Check, ChevronDown } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";

import { ClearButton, controlClass, controlHeight } from "@/components/ui/field";
import { popoverStyle, usePopoverPosition } from "@/hooks/use-popover-position";
import { cn } from "@/utils/utils";

export interface SelectChoice {
  value: string;
  label: string;
}

/**
 * A picker over a fixed list of choices, drawn by us.
 *
 * A native `<select>` opens the operating system's own menu — dark grey on
 * macOS, its own type, its own blue selection — so a filter that matched the
 * app everywhere else fell out of it the moment it was opened. The list here
 * wears the app's surfaces and its accent.
 *
 * Positioned like every other popover in the app: fixed, from the trigger's
 * rect, so the filters modal's scrolling body cannot clip it.
 *
 * Worked from the keyboard the way `FkSelect` is: Enter or a down arrow opens
 * it on whatever is chosen, the arrows walk the list, Enter takes the row
 * under the cursor, Escape leaves the value alone, and Tab moves on. Focus
 * never leaves the trigger — the row it is sitting on is named by
 * `aria-activedescendant` — so nothing is lost by closing the list.
 *
 * For a list that has to be searched or paged, use `FkSelect` — this one is
 * for a handful of known values, so it has neither.
 *
 * Pass an array as `value` and it picks several: the menu stays open, each
 * choice toggles, and the button reads them as one line. A filter the endpoint
 * answers with a list is one control either way, so this is a mode rather than
 * a second component.
 */
export function OptionSelect<V extends string | string[]>({
  id,
  value,
  onChange,
  choices,
  placeholder = "Select…",
  disabled = false,
  invalid = false,
  required = false,
  clearable = true,
  bare = false,
  fieldSize = "md",
  className,
  "aria-label": label,
}: {
  id?: string | undefined;
  /** A string picks one; an array picks several. The generic carries which
   *  back to the caller, so `onChange` is handed the same shape it was given. */
  value: V;
  onChange: (value: V) => void;
  choices: SelectChoice[];
  placeholder?: string;
  disabled?: boolean;
  /** Draws the refused border, as `FkSelect` does, for a form that validates. */
  invalid?: boolean | undefined;
  /** Blocks a submit while nothing is chosen, as a native select would. */
  required?: boolean | undefined;
  /** Off for a control that must always hold a value, such as a page size. */
  clearable?: boolean | undefined;
  /**
   * Drops the field shell — border, surface, focus ring — and leaves the value
   * and its chevron as text that happens to open a list. For a picker that sits
   * inside a sentence rather than in a form, such as the "25 / page" in a
   * pager: a box around the number there reads as a second control beside the
   * words, when it is the words' own noun.
   *
   * Still a button, still focusable, and still ringed on keyboard focus — what
   * goes is the resting chrome, not the affordance.
   */
  bare?: boolean | undefined;
  fieldSize?: keyof typeof controlHeight;
  className?: string | undefined;
  /** For a control with no visible label of its own, such as a page size. */
  "aria-label"?: string | undefined;
}) {
  const root = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const box = usePopoverPosition(root, open);

  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      const target = event.target as Node;
      // The list hangs outside the root, so both count as "inside".
      if (!root.current?.contains(target) && !menu.current?.contains(target)) setOpen(false);
    };
    const key = (event: globalThis.KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const many = Array.isArray(value);
  // Always a list of the chosen values, whichever shape came in, so the rest
  // of this component is written once.
  const picked: string[] = many ? (value as string[]) : value === "" ? [] : [value as string];
  const chosen = picked
    .map((one) => choices.find((choice) => choice.value === one)?.label ?? one)
    .join(", ");

  // Just the choices. A blank first row repeating the placeholder reads as a
  // value you could pick — it even takes the tick while nothing is chosen —
  // and is the one row that answers nothing. Emptying the control is the
  // clear button's job, where it says what it does.
  const rows: SelectChoice[] = choices;
  /** The row the arrows are sitting on. Opening puts it on what is chosen. */
  const [active, setActive] = useState(0);
  const at = (one: string) =>
    Math.max(
      rows.findIndex((row) => row.value === one),
      0,
    );

  /**
   * Toggling in multi mode, replacing in single. Sorted, so the same set of
   * choices is the same value however it was clicked — which is what makes a
   * saved set match and a query key stable.
   *
   * The casts are the price of one component serving both: `many` is what
   * decides the shape, and the checker cannot see that it agrees with `V`.
   */
  const pick = (next: string) => {
    if (!many) return onChange(next as unknown as V);
    const kept = picked.includes(next)
      ? picked.filter((one) => one !== next)
      : [...picked, next];
    onChange([...kept].sort() as unknown as V);
  };

  /** Taking a row, whether it was clicked or the cursor was sitting on it. */
  const take = (choice: SelectChoice) => {
    pick(choice.value);
    // Several values are picked in one visit; one is not.
    if (!many) setOpen(false);
  };

  const show = (index: number) => {
    setActive(index);
    // The row may be below the fold of a list that scrolls.
    menu.current
      ?.querySelectorAll("li > button")
      [index]?.scrollIntoView?.({ block: "nearest" });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const { key } = event;
    if (!open) {
      // Enter and Space would click the trigger anyway; taking them here means
      // the list opens on the chosen row rather than on the top of the list.
      if (key === "Enter" || key === " " || key === "ArrowDown" || key === "ArrowUp") {
        event.preventDefault();
        setActive(at(picked[0] ?? ""));
        setOpen(true);
      }
      return;
    }
    if (key === "ArrowDown" || key === "ArrowUp") {
      event.preventDefault();
      const step = key === "ArrowDown" ? 1 : -1;
      show((active + step + rows.length) % rows.length);
      return;
    }
    if (key === "Home" || key === "End") {
      event.preventDefault();
      show(key === "Home" ? 0 : rows.length - 1);
      return;
    }
    if (key === "Enter" || key === " ") {
      event.preventDefault();
      const choice = rows[active];
      if (choice) take(choice);
      return;
    }
    if (key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }
    // Tab belongs to the form: the list closes and the next field is reached
    // in the one press, with whatever was already chosen left standing.
    if (key === "Tab") setOpen(false);
  };

  return (
    <div ref={root} className={cn("relative", className)}>
      <button
        type="button"
        id={id}
        aria-label={label}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-invalid={invalid || undefined}
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        disabled={disabled}
        onClick={() => {
          setActive(at(picked[0] ?? ""));
          setOpen((was) => !was);
        }}
        onKeyDown={onKeyDown}
        className={cn(
          bare
            ? [
                "rounded-field text-fg w-full outline-none",
                "transition-[color,background-color] duration-[var(--duration-fast)]",
                "hover:text-fg disabled:text-fg-subtle disabled:cursor-not-allowed",
                "focus-visible:outline-2 focus-visible:outline-offset-2",
                "focus-visible:outline-[var(--accent)]",
              ].join(" ")
            : controlClass,
          controlHeight[fieldSize],
          "flex items-center gap-2 text-left",
          // The open state is drawn by the border, which a bare trigger has
          // not got; the list hanging under it is what says it is open.
          !bare && open && "border-accent ring-[3px] ring-[var(--accent-ring)]",
        )}
      >
        <span className={cn("min-w-0 flex-1 truncate", !chosen && "text-fg-subtle")}>
          {chosen || placeholder}
        </span>
        {/* Emptying a select is a choice the list cannot offer on every screen:
            the blank first row does it, but only once the list is open. This
            says so from the outside, as `FkSelect` already did. */}
        {clearable && picked.length > 0 && !disabled ? (
          <ClearButton
            label="Clear"
            onClear={() => onChange((many ? [] : "") as unknown as V)}
          />
        ) : null}
        <ChevronDown className="text-fg-subtle size-4 shrink-0" aria-hidden />
      </button>
      {/* Native validity for the form: empty and required blocks submit, the
          same trick `FkSelect` plays, because a button has no validity of
          its own and this is no longer a `<select>`. */}
      <input
        tabIndex={-1}
        aria-hidden
        data-validity-for={id}
        required={required}
        disabled={disabled}
        value={picked.join(",")}
        onChange={() => undefined}
        className="pointer-events-none absolute inset-x-3 bottom-0 h-px opacity-0"
      />

      {open ? (
        <div
          ref={menu}
          style={popoverStyle(box)}
          className="border-line bg-surface-raised animate-sheet fixed z-50 origin-top overflow-hidden rounded-xl border shadow-[var(--shadow-float)]"
        >
          <ul id={listId} role="listbox" className="max-h-72 overflow-y-auto p-1">
            {rows.map((choice, index) => {
              const selected = picked.includes(choice.value);
              return (
                <li key={choice.value} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    id={`${listId}-${index}`}
                    // The trigger keeps the focus; a row taking it would close
                    // the list on the way in.
                    tabIndex={-1}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => take(choice)}
                    className={cn(
                      "text-callout flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors",
                      selected
                        ? "bg-accent-soft text-accent font-medium"
                        : index === active
                          ? "bg-surface-inset text-fg"
                          : "text-fg hover:bg-surface-inset",
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{choice.label}</span>
                    {selected ? <Check className="size-4 shrink-0" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
